/** @jsx jsx */

import { Fragment } from 'react';
import { css, jsx } from '@emotion/core';

import { DateTime } from '@zapier/date';
import { Grid, Tag, TooltipWrapper } from '@zapier/design-system';

import Link from 'app/common/components/Link';
import StatusLabel from 'app/developer-v3/components/StatusLabel';
import { APP_STATUSES } from 'app/developer-v3/constants';
import {
  AppStatusName,
  SubmitStatus,
  VersionLifecycleStatus,
} from 'app/developer-v3/types/statuses';
import { getAppUrl } from 'app/developer-v3/utils';

import { STATUS_LABEL_VARIANT_MAP, STATUS_TOOLTIP_MAP } from './constants';

import type { Version } from './types';

const Styles = {
  tooltip: css`
    min-width: 200px;
  `,
};

type Props = {
  status: AppStatusName;
  version: Version;
  versionStatusLabel: string;
};

function ColumnStatus({ status, version, versionStatusLabel }: Props) {
  return (
    <Grid alignItems="center" columns="repeat(2, max-content)" gap="5px">
      <TooltipWrapper
        allowMultilineTooltip={true}
        content={
          <div css={Styles.tooltip}>
            {STATUS_TOOLTIP_MAP[version.lifecycle.status]}

            {version.lifecycle.status ===
              VersionLifecycleStatus.deprecating && (
              <Fragment>
                <br />
                Deprecating on <DateTime date={version.deprecationDate} />
              </Fragment>
            )}

            {version.lifecycle.status === VersionLifecycleStatus.deprecated && (
              <Fragment>
                <br />
                Deprecated on <DateTime date={version.deprecationDate} />
              </Fragment>
            )}
          </div>
        }
      >
        {() => (
          <StatusLabel
            label={versionStatusLabel}
            variant={STATUS_LABEL_VARIANT_MAP[version.lifecycle.status]}
          />
        )}
      </TooltipWrapper>
      {/* Add a tooltip for pushed back status */}
      {status === APP_STATUSES.pending.name &&
        version.submitStatus === SubmitStatus.pushedBack && (
          <TooltipWrapper
            allowMultilineTooltip={true}
            content={
              <div css={Styles.tooltip}>
                During our review we found some issues with this version. Create
                a new editable version by{' '}
                <Link href={`${getAppUrl(version.integrationId)}/versions`}>
                  cloning your integration
                </Link>
                .
              </div>
            }
          >
            {childProps => (
              <Tag variant="inverted" {...childProps}>
                Review Denied
              </Tag>
            )}
          </TooltipWrapper>
        )}
    </Grid>
  );
}

export default ColumnStatus;
